/**
 * Sample financial data used by every dashboard preview on the site.
 * One fictional company ("Arden & Co.") so every number agrees across
 * widgets, story panels, feature previews and the workspace.
 */

export const CHART_COLORS = {
    burgundy: '#391A18',
    gold: '#C8AE82',
    sage: '#8E9A86',
    terracotta: '#A65A49',
    sand: '#D6CFBD',
    stone: '#7D796F'
}

export const COMPANY_NAME = 'Arden & Co.'
export const PERIOD_LABEL = 'September 2026'

export const MONTHS = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep']

export const MONTHS_FULL = [
    'October 2025',
    'November 2025',
    'December 2025',
    'January 2026',
    'February 2026',
    'March 2026',
    'April 2026',
    'May 2026',
    'June 2026',
    'July 2026',
    'August 2026',
    'September 2026'
]

export const INCOME_SERIES = [352180, 361420, 389760, 371050, 384330, 402210, 418640, 409120, 431870, 447360, 445780, 482340]
export const EXPENSE_SERIES = [291340, 296820, 318450, 302180, 299640, 311270, 306190, 314560, 309210, 322480, 327060, 316920]
export const CASH_SERIES = [1367300, 1431900, 1503210, 1572080, 1656770, 1747710, 1860160, 1954720, 2077380, 2202260, 2320980, 2486400]
export const NET_SERIES = INCOME_SERIES.map((income, index) => income - EXPENSE_SERIES[index])

export const SUBSCRIPTION_SHARE = 0.72

export const TOTAL_INCOME = 482340
export const TOTAL_EXPENSES = 316920
export const NET_CASH_FLOW = 165420
export const CASH_BALANCE = 2486400

export const INCOME_CHANGE = 8.2
export const EXPENSE_CHANGE = -3.1
export const NET_CHANGE = 39.3
export const CASH_CHANGE = 7.1

export type Category = {
    name: string
    value: number
    color: string
}

export const EXPENSE_CATEGORIES: Array<Category> = [
    { name: 'Payroll', value: 145780, color: CHART_COLORS.burgundy },
    { name: 'Software & cloud', value: 50710, color: CHART_COLORS.gold },
    { name: 'Marketing', value: 44370, color: CHART_COLORS.sage },
    { name: 'Operations', value: 41200, color: CHART_COLORS.terracotta },
    { name: 'Other', value: 34860, color: CHART_COLORS.sand }
]

export const INCOME_SOURCES: Array<Category> = [
    { name: 'Subscriptions', value: 347285, color: CHART_COLORS.burgundy },
    { name: 'Services', value: 91645, color: CHART_COLORS.gold },
    { name: 'Other', value: 43410, color: CHART_COLORS.sand }
]

export type Transaction = {
    id: string
    merchant: string
    category: string
    amount: number
    date: string
    status: 'Matched' | 'Reconciled' | 'Review'
}

export const TRANSACTIONS: Array<Transaction> = [
    { id: 'aws', merchant: 'Amazon Web Services', category: 'Software & cloud', amount: -12480, date: '2026-09-30', status: 'Matched' },
    { id: 'harbor', merchant: 'Harbor & Pine', category: 'Subscription revenue', amount: 24000, date: '2026-09-30', status: 'Reconciled' },
    { id: 'payroll', merchant: 'Gusto payroll run', category: 'Payroll', amount: -71240.18, date: '2026-09-29', status: 'Reconciled' },
    { id: 'figma', merchant: 'Figma', category: 'Software & cloud', amount: -1350, date: '2026-09-29', status: 'Matched' },
    { id: 'kestrel', merchant: 'Kestrel Analytics', category: 'Services revenue', amount: 18750, date: '2026-09-28', status: 'Reconciled' },
    { id: 'linkedin', merchant: 'LinkedIn Ads', category: 'Marketing', amount: -6800, date: '2026-09-27', status: 'Review' },
    { id: 'office', merchant: 'Mission Street office', category: 'Operations', amount: -9400, date: '2026-09-26', status: 'Matched' }
]

export const LIVE_FEED: Array<Transaction> = [
    { id: 'feed-1', merchant: 'Notion', category: 'Software & cloud', amount: -960, date: '2026-10-01', status: 'Matched' },
    { id: 'feed-2', merchant: 'Northfield Partners', category: 'Subscription revenue', amount: 12500, date: '2026-10-01', status: 'Reconciled' },
    { id: 'feed-3', merchant: 'Delta Air Lines', category: 'Travel', amount: -1284.6, date: '2026-10-01', status: 'Matched' },
    { id: 'feed-4', merchant: 'Google Ads', category: 'Marketing', amount: -4200, date: '2026-10-01', status: 'Review' },
    { id: 'feed-5', merchant: 'Blue Bottle Coffee', category: 'Meals', amount: -86.4, date: '2026-10-01', status: 'Matched' },
    { id: 'feed-6', merchant: 'Datadog', category: 'Software & cloud', amount: -3150, date: '2026-10-01', status: 'Matched' },
    { id: 'feed-7', merchant: 'Lattice Studio', category: 'Services revenue', amount: 8600, date: '2026-10-01', status: 'Reconciled' },
    { id: 'feed-8', merchant: 'WeWork', category: 'Operations', amount: -2750, date: '2026-10-01', status: 'Matched' }
]

export type Insight = {
    id: string
    title: string
    body: string
    action: string
    tone: 'positive' | 'warning' | 'neutral'
}

export const INSIGHTS: Array<Insight> = [
    {
        id: 'software',
        title: 'Software spend is up 18% this quarter',
        body: 'Three project management tools overlap. Consolidating them could save about $4,200 a month.',
        action: 'Review tools',
        tone: 'warning'
    },
    {
        id: 'collections',
        title: 'Collections are speeding up',
        body: 'Enterprise customers now pay 6 days faster than last quarter, adding $310K to projected Q4 cash.',
        action: 'View forecast',
        tone: 'positive'
    },
    {
        id: 'marketing',
        title: 'Marketing is trending over budget',
        body: 'At the current pace, Q4 marketing spend lands 9% over plan. LinkedIn Ads drove most of the increase.',
        action: 'Adjust budget',
        tone: 'neutral'
    }
]

export const PERFORMANCE = [
    { label: 'Gross margin', value: 72.4, display: '72.4%', progress: 0.724, note: 'Target 75%' },
    { label: 'Operating margin', value: 34.3, display: '34.3%', progress: 0.686, note: 'Target 30%' },
    { label: 'Revenue growth', value: 8.2, display: '+8.2%', progress: 0.82, note: 'Month over month' },
    { label: 'Days sales outstanding', value: 38, display: '38 days', progress: 0.62, note: 'Down from 44' }
]

export const FORECAST_LABELS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']
export const FORECAST_ACTUAL = [1860160, 1954720, 2077380, 2202260, 2320980, 2486400]

export type ScenarioKey = 'base' | 'conservative' | 'growth'

export const FORECAST_SCENARIOS: Record<ScenarioKey, { label: string, values: Array<number>, note: string }> = {
    base: {
        label: 'Base case',
        values: [2618000, 2741000, 2829000, 2967000, 3104000, 3242000],
        note: 'Current growth and spend continue as planned.'
    },
    conservative: {
        label: 'Conservative',
        values: [2571000, 2638000, 2671000, 2742000, 2806000, 2868000],
        note: 'Collections slow by 10 days and two renewals slip a quarter.'
    },
    growth: {
        label: 'Growth',
        values: [2652000, 2815000, 2944000, 3121000, 3302000, 3489000],
        note: 'Pipeline converts on schedule and four hires join in November.'
    }
}

export const buildForecast = (key: ScenarioKey) => {
    const splitIndex = FORECAST_ACTUAL.length - 1
    const values = [...FORECAST_ACTUAL, ...FORECAST_SCENARIOS[key].values]
    const spread = (index: number): number => Math.max(0, index - splitIndex) * 0.02
    const low = values.map((value, index) => value * (1 - spread(index)))
    const high = values.map((value, index) => value * (1 + spread(index)))

    return { values, low, high, splitIndex }
}

export type Budget = {
    team: string
    spent: number
    budget: number
    projected: number
}

export const BUDGETS: Record<'current' | 'previous', Array<Budget>> = {
    current: [
        { team: 'Engineering', spent: 412000, budget: 480000, projected: 468000 },
        { team: 'Marketing', spent: 138000, budget: 150000, projected: 163500 },
        { team: 'Sales', spent: 205000, budget: 260000, projected: 251000 },
        { team: 'Operations', spent: 96000, budget: 120000, projected: 114000 },
        { team: 'Customer success', spent: 74000, budget: 90000, projected: 86500 }
    ],
    previous: [
        { team: 'Engineering', spent: 451000, budget: 460000, projected: 451000 },
        { team: 'Marketing', spent: 139500, budget: 140000, projected: 139500 },
        { team: 'Sales', spent: 238000, budget: 250000, projected: 238000 },
        { team: 'Operations', spent: 117000, budget: 115000, projected: 117000 },
        { team: 'Customer success', spent: 81000, budget: 85000, projected: 81000 }
    ]
}

export const REVENUE_METRICS = [
    { label: 'MRR', value: 347285, format: 'compact' as const, change: 6.4 },
    { label: 'ARR', value: 4167420, format: 'compact' as const, change: 6.4 },
    { label: 'Net revenue retention', value: 118, format: 'percent' as const, change: 3.0 },
    { label: 'New MRR', value: 28400, format: 'compact' as const, change: 12.8 }
]

export type ReportLine = {
    label: string
    value: number
    note?: string
    emphasis?: boolean
}

export const REPORTS: Record<'pnl' | 'balance' | 'cashflow', { title: string, subtitle: string, lines: Array<ReportLine> }> = {
    pnl: {
        title: 'Profit and loss',
        subtitle: 'September 2026',
        lines: [
            { label: 'Revenue', value: 482340, note: '+8.2%' },
            { label: 'Cost of revenue', value: -133126, note: '27.6%' },
            { label: 'Gross profit', value: 349214, note: '72.4%', emphasis: true },
            { label: 'Operating expenses', value: -183794, note: '38.1%' },
            { label: 'Operating income', value: 165420, note: '34.3%', emphasis: true },
            { label: 'Net income', value: 151180, note: '31.3%', emphasis: true }
        ]
    },
    balance: {
        title: 'Balance sheet',
        subtitle: 'As of September 30, 2026',
        lines: [
            { label: 'Cash and equivalents', value: 2486400 },
            { label: 'Accounts receivable', value: 612300 },
            { label: 'Total assets', value: 3418900, emphasis: true },
            { label: 'Accounts payable', value: 284150 },
            { label: 'Deferred revenue', value: 726400 },
            { label: 'Total equity', value: 2163250, emphasis: true }
        ]
    },
    cashflow: {
        title: 'Cash flow statement',
        subtitle: 'September 2026',
        lines: [
            { label: 'Operating activities', value: 214860 },
            { label: 'Investing activities', value: -31200 },
            { label: 'Financing activities', value: -18240 },
            { label: 'Net change in cash', value: 165420, emphasis: true },
            { label: 'Opening cash', value: 2320980 },
            { label: 'Closing cash', value: 2486400, emphasis: true }
        ]
    }
}
